/**
 * What setup needs to know before the group can start rating, read from the
 * local store rather than the network.
 *
 * Setup happens the moment a game ends, which is the moment the room is loudest
 * and the wifi least reliable. Every default offered here — who is probably
 * playing, which aspects were on last time for this game — is computed from
 * rows already on the device. Nothing in this file blocks on a server.
 *
 * The ranking functions are pure and take their rows as arguments so the
 * ordering rules can be tested without a browser.
 */

import { getAll } from "../../store/idb.ts";
import { getLocale, localiseDefinition, localiseSnapshot } from "../../locale.ts";
import { normalise } from "../../../shared/title-key.ts";
import type {
  Aspect,
  Game,
  PlaySession,
  Profile,
  SessionAspect,
  SessionParticipant,
  SubQuestion,
} from "../../../shared/types.ts";

export interface SetupData {
  profiles: Profile[];
  aspects: Aspect[];
  games: Game[];
  sessions: PlaySession[];
  participants: SessionParticipant[];
  sessionAspects: SessionAspect[];
  subquestions: SubQuestion[];
}

export async function loadSetupData(): Promise<SetupData> {
  const [profiles, aspects, games, sessions, participants, sessionAspects, subquestions] =
    await Promise.all([
      getAll<Profile>("profile"),
      getAll<Aspect>("aspect"),
      getAll<Game>("game"),
      getAll<PlaySession>("play_session"),
      getAll<SessionParticipant>("session_participant"),
      getAll<SessionAspect>("session_aspect"),
      getAll<SubQuestion>("subquestion"),
    ]);
  // Read once, not per row: setup is a screen the group is standing over, and the
  // language cannot change while it is open — the only way to change it is to
  // leave for settings, which unmounts this.
  const locale = getLocale();
  return {
    profiles,
    aspects: aspects.map((row) => localiseDefinition(row, locale)),
    games,
    sessions,
    participants,
    sessionAspects: sessionAspects.map((row) => localiseSnapshot(row, locale)),
    subquestions: subquestions.map((row) => localiseDefinition(row, locale)),
  };
}

const ACTIVE = <T extends { archived_at?: number | null; deleted?: 0 | 1 }>(row: T): boolean =>
  row.deleted !== 1 && !row.archived_at;

/**
 * Roster order: who you actually play with, most often and most recently.
 *
 * The ordering itself is NOT computed here — `coPlayerStandings()` in the store
 * owns it, so every screen that shows co-players sorts the same way. This only
 * projects that order onto the profile rows the picker renders, and appends
 * whoever has never played: a profile created five minutes ago has no standing
 * and would otherwise silently vanish from the one screen that could use it.
 */
export function orderRoster(
  profiles: readonly Profile[],
  standings: readonly { profile_id: string }[],
): Profile[] {
  const rank = new Map(standings.map((s, i) => [s.profile_id, i] as const));
  const live = profiles.filter(ACTIVE);
  const played = live
    .filter((p) => rank.has(p.id))
    .sort((a, b) => rank.get(a.id)! - rank.get(b.id)!);
  const unplayed = live
    .filter((p) => !rank.has(p.id))
    .sort((a, b) => a.name.localeCompare(b.name));
  return [...played, ...unplayed];
}

/** Starred aspects: the default active set the plan puts at three. */
export function defaultAspectIds(aspects: readonly Aspect[]): string[] {
  return aspects
    .filter(ACTIVE)
    .filter((a) => a.starred === 1)
    .sort((a, b) => a.sort - b.sort)
    .map((a) => a.id);
}

export interface Recall {
  session_id: string;
  played_at: number;
  aspect_ids: string[];
  profile_ids: string[];
}

/**
 * "Same as last time" for this game — the single biggest friction reducer in
 * the flow, because a group that has already decided which axes matter for
 * Brass should not be asked again every fortnight.
 *
 * Reads the snapshot rows, not the live taxonomy: the answer to "what did we
 * have on last time" is what was frozen into that session, and an aspect that
 * has since been archived is correctly absent from the offer.
 */
export function recallForGame(
  gameId: string,
  data: Pick<SetupData, "sessions" | "sessionAspects" | "participants">,
  activeAspectIds?: ReadonlySet<string>,
): Recall | null {
  const previous = data.sessions
    .filter((s) => s.deleted !== 1 && s.game_id === gameId && s.status === "COMPLETED")
    .sort((a, b) => b.played_at - a.played_at)[0];
  if (!previous) return null;

  const aspect_ids = data.sessionAspects
    .filter((sa) => sa.deleted !== 1 && sa.session_id === previous.id)
    .sort((a, b) => a.sort_snapshot - b.sort_snapshot)
    .map((sa) => sa.aspect_id)
    .filter((id) => !activeAspectIds || activeAspectIds.has(id));

  const profile_ids = data.participants
    .filter((p) => p.deleted !== 1 && p.session_id === previous.id)
    .map((p) => p.profile_id);

  return {
    session_id: previous.id,
    played_at: previous.played_at,
    aspect_ids,
    profile_ids,
  };
}

/**
 * Local title search, so picking a game never waits on BGG.
 *
 * Matched on the same folded key the bundled catalogue uses, and that agreement
 * is the whole point rather than a tidiness preference. This was a plain
 * `title.toLowerCase().includes(query)`, which cannot match across punctuation:
 * `"brass: birmingham".includes("brass birm")` is false. So typing `brass birm`
 * found nothing on the shelf of somebody who owns Brass: Birmingham — and it got
 * worse from there, because the server *did* find it and `Setup.tsx` then
 * discarded that hit as "already in your library". A game the group had played
 * repeatedly could only be re-added through the manual-entry row.
 *
 * Ranking is by where the match falls, so a title that starts with what was
 * typed comes before one that merely contains it, and ties break alphabetically
 * rather than arbitrarily.
 */
export function searchLocalGames(games: readonly Game[], query: string, limit = 8): Game[] {
  const q = normalise(query);
  const live = games.filter((g) => g.deleted !== 1);
  // `live` is already a fresh array from `.filter()`, so sorting it in place is
  // not sorting the caller's data.
  if (!q) return live.sort((a, b) => b.updated_at - a.updated_at).slice(0, limit);

  // Keyed once per candidate rather than inside the comparator, which would
  // refold the same title on every comparison.
  const scored: { game: Game; at: number }[] = [];
  for (const game of live) {
    const at = normalise(game.title).indexOf(q);
    if (at !== -1) scored.push({ game, at });
  }

  return scored
    .sort((a, b) => {
      if (a.at !== b.at) return a.at - b.at;
      return a.game.title.localeCompare(b.game.title);
    })
    .slice(0, limit)
    .map((s) => s.game);
}

/**
 * The follow-ups each aspect will bring into the night, by aspect id.
 *
 * The group never picks these separately — the server derives them from the
 * chosen aspects when the night starts. This is the setup screen's copy of that
 * same rule, and it exists for one reason: so an aspect chip can say how many
 * extra questions come with it. A sub-question that turned up in the rating loop
 * unannounced would be a surprise, and a second picker at a table with friends
 * waiting would be friction; a count on the chip is the honest cheap middle.
 *
 * Same `ACTIVE` predicate the aspect list uses, so an archived or deleted
 * follow-up is absent from the count exactly as it is absent from the night.
 */
export function activeSubQuestionsByAspect(
  subquestions: readonly SubQuestion[],
): Map<string, SubQuestion[]> {
  const byAspect = new Map<string, SubQuestion[]>();
  for (const sq of subquestions.filter(ACTIVE).sort((a, b) => a.sort - b.sort)) {
    const list = byAspect.get(sq.aspect_id);
    if (list) list.push(sq);
    else byAspect.set(sq.aspect_id, [sq]);
  }
  return byAspect;
}

export function activeAspects(aspects: readonly Aspect[]): Aspect[] {
  return aspects
    .filter(ACTIVE)
    .slice()
    .sort((a, b) => a.sort - b.sort);
}
